<template>
  <div class="app-header">
    <div class="app-header__heading">
      <h2 class="app-header__title">
        <slot name="title">{{ title }}</slot>
      </h2>
      <p v-if="hasDescription" class="app-header__description">
        <slot name="description">{{ description }}</slot>
      </p>
    </div>

    <div v-if="$slots.default" class="app-header__actions">
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * Page header — title + optional description on the left, action slot on the
 * right. Used at the top of admin screens to keep the heading/actions layout
 * consistent across pages.
 *
 * Props:
 *   title        — heading text (or override via the `title` slot).
 *   description  — secondary line under the title (or `description` slot).
 *
 * Slots:
 *   title        — replaces the title text entirely.
 *   description  — replaces the description text entirely.
 *   default      — right-aligned actions (buttons). Hidden when empty.
 */
export default {
  name: 'AppHeader',

  props: {
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
  },

  computed: {
    hasDescription() {
      return !!this.description || !!this.$slots.description
    },
  },
}
</script>

<style lang="scss" scoped>
// Self-contained tokens — keep the component portable (mirrors the admin
// `$ad-*` palette without importing the dashboard theme module).
$ah-text:       #2b3a4a;
$ah-text-muted: #6c7a89;

.app-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  &__title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: $ah-text;
    line-height: 1.3;
  }

  &__description {
    margin: 2px 0 0;
    font-size: 13px;
    color: $ah-text-muted;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
}
</style>
